import {
  matchPath,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Routes } from '@/constants';
import {
  selectUserCompliance,
  useAppSelector,
  useFetchLearningPathQuery,
} from '@/store';

import { SIDEBAR_ITEMS } from '../useSidebarItems';

type Crumb = { label: string; to?: string };

const getTopicFromSearch = (search?: string) => {
  if (!search) return null;
  return new URLSearchParams(search).get('topic');
};

export const useLpBreadcrumbs = (): Crumb[] => {
  const { pathname, state } = useLocation();
  const [searchParams] = useSearchParams();
  const { learningPathId } = useParams();
  const compliance = useAppSelector(selectUserCompliance);

  const selectedTopic = searchParams.get('topic');
  const { from } = state || {};
  const parsedLearningPathId = Number(learningPathId);
  const isDetailRoute = !!matchPath(Routes.LEARNING_PATH, pathname);

  const { data: learningPath } = useFetchLearningPathQuery(
    {
      compliance,
      learningPathId: parsedLearningPathId,
    },
    { skip: !isDetailRoute || !parsedLearningPathId },
  );

  const routes: Crumb[] = [
    { label: 'Learning Paths', to: Routes.LEARNING_PATHS },
  ];

  if (isDetailRoute) {
    const fromPathname = from?.pathname;
    const fromTopic = getTopicFromSearch(from?.search);

    if (fromPathname === Routes.LEARNING_PATHS_IN_PROGRESS) {
      routes.push({
        label: SIDEBAR_ITEMS.IN_PROGRESS,
        to: Routes.LEARNING_PATHS_IN_PROGRESS,
      });
    } else if (fromPathname === Routes.LEARNING_PATHS_RECOMMENDED) {
      routes.push({
        label: SIDEBAR_ITEMS.RECOMMENDED,
        to: Routes.LEARNING_PATHS_RECOMMENDED,
      });
    } else if (fromPathname === Routes.LEARNING_PATHS_TOPICS) {
      if (fromTopic) {
        routes.push({
          label: SIDEBAR_ITEMS.ALL_TOPICS,
          to: Routes.LEARNING_PATHS_TOPICS,
        });
        routes.push({
          label: fromTopic,
          to: `${Routes.LEARNING_PATHS_TOPICS}?topic=${encodeURIComponent(fromTopic)}`,
        });
      } else {
        routes.push({
          label: SIDEBAR_ITEMS.ALL_TOPICS,
          to: Routes.LEARNING_PATHS_TOPICS,
        });
      }
    }

    if (learningPath?.title) {
      routes.push({ label: learningPath.title });
    }

    return routes;
  }

  if (pathname === Routes.LEARNING_PATHS_IN_PROGRESS) {
    routes.push({ label: SIDEBAR_ITEMS.IN_PROGRESS });
    return routes;
  }

  if (pathname === Routes.LEARNING_PATHS_RECOMMENDED) {
    routes.push({ label: SIDEBAR_ITEMS.RECOMMENDED });
    return routes;
  }

  if (pathname === Routes.LEARNING_PATHS_TOPICS) {
    if (selectedTopic) {
      routes.push({
        label: SIDEBAR_ITEMS.ALL_TOPICS,
        to: Routes.LEARNING_PATHS_TOPICS,
      });
      routes.push({ label: selectedTopic });
      return routes;
    } else {
      routes.push({ label: SIDEBAR_ITEMS.ALL_TOPICS });
      return routes;
    }
  }

  return routes;
};
